import { Fragment, type ReactNode } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import FaIcon from './FaIcon';
import Button from './Button';

type ConfirmDialogVariant = 'danger' | 'warning' | 'info';

const VARIANT_CONFIG: Record<ConfirmDialogVariant, {
  iconBg: string;
  iconColor: string;
  faIcon: string;
  buttonVariant: 'danger' | 'warning' | 'primary';
}> = {
  danger: {
    iconBg: 'bg-red-100 dark:bg-red-900/30',
    iconColor: 'text-red-600 dark:text-red-400',
    faIcon: 'triangle-exclamation',
    buttonVariant: 'danger',
  },
  warning: {
    iconBg: 'bg-amber-100 dark:bg-amber-900/30',
    iconColor: 'text-amber-600 dark:text-amber-400',
    faIcon: 'triangle-exclamation',
    buttonVariant: 'warning',
  },
  info: {
    iconBg: 'bg-primary-100 dark:bg-primary-900/30',
    iconColor: 'text-primary-600 dark:text-primary-400',
    faIcon: 'circle-info',
    buttonVariant: 'primary',
  },
};

interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  /**
   * Optional handler for *dismissal* gestures only (Escape key, backdrop click).
   * Headless UI reports both through the same channel as the cancel button, so by
   * default they all run `onClose`. Supply `onDismiss` when the cancel action is
   * DESTRUCTIVE (e.g. "Discard draft"): a reflexive Escape must never be read as
   * consent to destroy data. Omitting it keeps the historical behaviour, so every
   * existing call site is unaffected.
   */
  onDismiss?: () => void;
  onConfirm: () => void;
  title: string;
  /** Inline JSX is allowed (e.g. <strong>name</strong>) — rendered inside a <p>, so keep it inline-level */
  message: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  variant?: ConfirmDialogVariant;
  /** @deprecated Use variant instead */
  confirmButtonClass?: string;
  isLoading?: boolean;
}

/**
 * Accessible confirmation dialog component.
 * Uses Headless UI for focus trapping, transitions, and ARIA support.
 * Supports danger, warning, and info variants with consistent styling.
 */
export default function ConfirmDialog({
  isOpen,
  onClose,
  onDismiss,
  onConfirm,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  variant = 'danger',
  confirmButtonClass,
  isLoading = false,
}: ConfirmDialogProps) {
  const config = VARIANT_CONFIG[variant];

  const handleConfirm = () => {
    onConfirm();
  };

  // Destructive confirmations should announce as alertdialog so screen readers
  // treat the prompt with the urgency it deserves. info/warning use the
  // standard dialog role.
  const dialogRole: 'dialog' | 'alertdialog' = variant === 'danger' ? 'alertdialog' : 'dialog';

  return (
    <Transition appear show={isOpen} as={Fragment}>
      {/*
        Headless UI routes Escape AND backdrop clicks into this one `onClose`.
        Callers whose cancel button destroys data pass `onDismiss` so a reflexive
        Escape means "leave it alone" instead of silently running the destructive
        cancel path. Without the `onDismiss ?? onClose` split, hitting Escape on a
        prompt like "Unsaved Draft Found" deletes the recovered draft outright.
      */}
      <Dialog as="div" role={dialogRole} className="relative z-[9998]" onClose={onDismiss ?? onClose}>
        {/* Entrance only — close stays instant per motion doctrine (no leave transition). */}
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200 motion-reduce:transition-none"
          enterFrom="opacity-0"
          enterTo="opacity-100"
        >
          <div className="fixed inset-0 bg-black/50" aria-hidden="true" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200 motion-reduce:transition-none"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
          >
            <Dialog.Panel className="mx-auto max-w-md w-full rounded-lg bg-surface-primary shadow-xl">
              <div className="p-6">
                <div className="flex items-start gap-4">
                  <div className={`flex-shrink-0 w-12 h-12 ${config.iconBg} rounded-full flex items-center justify-center`}>
                    <FaIcon
                      icon={config.faIcon}
                      className={`w-6 h-6 ${config.iconColor}`}
                      title={variant}
                    />
                  </div>
                  <div className="flex-1">
                    <Dialog.Title className="text-lg font-semibold text-theme-primary">
                      {title}
                    </Dialog.Title>
                    <Dialog.Description className="mt-2 text-sm text-theme-secondary">
                      {message}
                    </Dialog.Description>
                  </div>
                </div>

                <div className="mt-6 flex items-center justify-end gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClose}
                    disabled={isLoading}
                  >
                    {cancelLabel}
                  </Button>
                  <Button
                    variant={config.buttonVariant}
                    size="sm"
                    onClick={handleConfirm}
                    disabled={isLoading}
                    loading={isLoading}
                    className={confirmButtonClass}
                  >
                    {confirmLabel}
                  </Button>
                </div>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
}
